/**
 * ============================================================================
 * [P3-01-B] SavedTab - 저장된 글 탭 메인 컴포넌트
 *
 * 역할:
 * - FilterBar와 TextList를 조합하여 저장된 글 관리
 * - useTexts Hook으로 데이터 관리
 * - 상세 보기 모달 상태 관리
 * - 클라이언트 사이드 필터링 (타입/검색)
 *
 * 구조:
 * - FilterBar: 검색 및 필터링 (세그먼트 컨트롤)
 * - TextList: 가상 스크롤 글 목록
 * - TextDetailModal: 상세 보기/편집
 * ============================================================================
 */

import { useState, useMemo } from 'react'
import { useTexts } from '@/hooks'
import { useAppStore } from '@/stores'
import { FilterBar } from './FilterBar'
import { TextList } from './TextList'
import { TextDetailModal } from './TextDetailModal'
import { TrashBinModal } from './TrashBinModal'
import type { SavedText } from '@/types'
import styles from './Saved.module.css'

// ============================================================================
// Component
// ============================================================================
export function SavedTab() {
  // ========================================================================
  // useTexts Hook에서 상태/함수 가져오기
  // ========================================================================
  const {
    texts,
    deletedTexts,  // [P5-02] 휴지통 항목
    isLoading,
    error,
    hasMore,
    loadMore,
    setFilter,
    editText,
    removeText,
    toggleFavorite,  // [P3-01] 즐겨찾기 토글
    toggleUsed,      // 레퍼런스 사용 상태 토글
    restoreText,     // [P5-02] 휴지통 복원
    permanentDeleteText,  // [P5-02] 영구 삭제
  } = useTexts()

  // ========================================================================
  // 휴지통 모달 상태
  // ========================================================================
  const [isTrashBinOpen, setIsTrashBinOpen] = useState(false)

  // ========================================================================
  // Store에서 필터 상태 가져오기
  // ========================================================================
  const filter = useAppStore((state) => state.savedTexts.filter)

  // ========================================================================
  // 사용 가능한 주제 목록 추출
  // ========================================================================
  const availableTopics = useMemo(() => {
    // ================================================================
    // 주제 필터 — 타입 필터(전체/작성/레퍼런스)에 연동
    // 선택된 타입에 해당하는 글들의 주제만 표시
    // ================================================================
    let base = texts.filter((t) => t.type !== 'script') // A안: script 제외

    // 타입 필터에 따른 주제 대상 글 선별
    if (filter.type && filter.type !== 'all') {
      if (filter.type === 'edit' || filter.type === 'edit-used') {
        base = base.filter((t) => t.type === 'edit')
      } else if (filter.type === 'reference' || filter.type === 'reference-used' || filter.type === 'reference-unused') {
        base = base.filter((t) => t.type === 'reference')
      }
    }

    const topics = base
      .map((t) => t.topic)
      .filter((topic): topic is string => !!topic && topic.trim() !== '')
    return [...new Set(topics)].sort()
  }, [texts, filter.type])

  // ========================================================================
  // 사용된 레퍼런스 ID 집합 계산
  // - edit 타입 글의 linkedReferences에 포함된 레퍼런스 ID를 수집
  // - 클라이언트 사이드에서 파생 (추가 Firebase 읽기 불필요)
  // ========================================================================
  const usedReferenceIds = useMemo(() => {
    const ids = new Set<string>()
    texts.forEach((t) => {
      if (t.type === 'edit' && t.linkedReferences) {
        t.linkedReferences.forEach((refId) => ids.add(refId))
      }
    })
    return ids
  }, [texts])

  // ========================================================================
  // 클라이언트 사이드 필터링
  // - 타입 필터: all, edit, edit-used, reference, reference-used, reference-unused
  // - 검색 필터: 제목/내용 포함
  // - 주제 필터: 특정 주제만 표시
  // ========================================================================
  const filteredTexts = useMemo(() => {
    // ================================================================
    // [A안] script 타입 제외 — 레거시와 동일하게 저장된 글/스크립트 분리
    // 스크립트 데이터는 "스크립트 작성" 탭(ManagementTab)에서만 관리
    // ================================================================
    let result = texts.filter((t) => t.type !== 'script')

    // 1. 타입 필터 적용
    if (filter.type && filter.type !== 'all') {
      if (filter.type === 'reference') {
        // "레퍼런스" 필터: 사용 안 된 레퍼런스만 표시
        result = result.filter((t) => t.type === 'reference' && !t.isUsed && !usedReferenceIds.has(t.id))
      } else if (filter.type === 'reference-used') {
        // "레퍼런스 사용됨" 필터: 수동 표시(isUsed) OR 자동 감지(linkedReferences)
        result = result.filter((t) => t.type === 'reference' && (t.isUsed || usedReferenceIds.has(t.id)))
      } else if (filter.type === 'edit') {
        // "작성" 필터: 사용 안 된 작성글만 표시 (레퍼런스 패턴 미러링)
        result = result.filter((t) => t.type === 'edit' && !t.isUsed)
      } else if (filter.type === 'edit-used') {
        // "작성 사용됨" 필터: 사용 표시된 작성글만 표시 (작성글은 수동 토글 전용)
        result = result.filter((t) => t.type === 'edit' && !!t.isUsed)
      } else {
        result = result.filter((t) => t.type === filter.type)
      }
    }

    // 2. 주제 필터 적용
    if (filter.topic) {
      result = result.filter((t) => t.topic === filter.topic)
    }

    // 2.5 레퍼런스 유형 필터 적용 (구조/아이디어)
    if (filter.referenceType && filter.referenceType !== 'all') {
      result = result.filter((t) => t.referenceType === filter.referenceType)
    }

    // 3. 검색 필터 적용
    if (filter.search?.trim()) {
      const searchLower = filter.search.toLowerCase()
      result = result.filter(
        (t) =>
          t.content?.toLowerCase().includes(searchLower) ||
          t.title?.toLowerCase().includes(searchLower) ||
          t.topic?.toLowerCase().includes(searchLower)
      )
    }

    return result
  }, [texts, filter.type, filter.topic, filter.search, filter.referenceType, usedReferenceIds])

  // ========================================================================
  // 모달 상태: 선택된 텍스트
  // ========================================================================
  const [selectedText, setSelectedText] = useState<SavedText | null>(null)

  // ========================================================================
  // 모달 닫기 핸들러
  // ========================================================================
  const handleCloseModal = () => setSelectedText(null)

  // ========================================================================
  // 에러 상태 표시
  // ========================================================================
  if (error) {
    return (
      <div className={styles.savedTab}>
        <div className={styles.errorState}>
          <p>⚠️ 데이터를 불러올 수 없습니다.</p>
          <p>{error.message}</p>
        </div>
      </div>
    )
  }

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.savedTab}>
      {/* ================================================================
          필터 바: 검색, 타입 필터, 정렬 + 휴지통 버튼
          ================================================================ */}
      <div className={styles.filterRow}>
        <FilterBar onFilterChange={setFilter} availableTopics={availableTopics} />
        <button
          type="button"
          className={styles.trashBinBtn}
          onClick={() => setIsTrashBinOpen(true)}
          aria-label="휴지통 열기"
        >
          🗑️ 휴지통 {deletedTexts.length > 0 && `(${deletedTexts.length})`}
        </button>
      </div>

      {/* ================================================================
          글 목록: 필터링된 결과
          ================================================================ */}
      <TextList
        texts={filteredTexts}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        onDelete={removeText}
        onSelect={setSelectedText}
        onToggleFavorite={toggleFavorite}
        onToggleUsed={toggleUsed}
        usedReferenceIds={usedReferenceIds}
      />

      {/* ================================================================
          상세 보기 모달
          - selectedText가 있을 때만 렌더링
          ================================================================ */}
      {selectedText && (
        <TextDetailModal
          text={selectedText}
          onClose={handleCloseModal}
          onEdit={editText}
          onDelete={removeText}
        />
      )}

      {/* ================================================================
          휴지통 모달
          ================================================================ */}
      <TrashBinModal
        isOpen={isTrashBinOpen}
        onClose={() => setIsTrashBinOpen(false)}
        deletedTexts={deletedTexts}
        onRestore={restoreText}
        onPermanentDelete={permanentDeleteText}
      />
    </div>
  )
}

export default SavedTab
